import { useRef, useState } from "react";
import { usePostHog } from "posthog-js/react";
import { ProviderType } from "@wags/catalog/pure.ts";
import type { ListingData } from "@/features/listings";
import {
    deriveListingHealth,
    type FeeInclusions,
    formatIncludedList,
    goodWithValue,
    joinParts,
    listingContactAnswers,
    listingFeeInclusions,
} from "@/features/listings";
import { usePreviewedExperiment } from "@/lib/core/experiments/use-experiment-arm";
import type { DogPageSectionsProps } from "./dog-page-sections";
import { useListingContactHelpPreview } from "./listing-contact-help-preview";
import {
    type ListingContactGap,
    listingContactGapSchema,
    listingContactHelpEventSchema,
    type ListingContactHelpEvent,
} from "./listing-contact-help.contract";

const GAP_ORDER = listingContactGapSchema.options;

const MAX_GAPS = 3;

type GetsAlongWith = Array<{
    type: "children" | "dogs" | "cats" | "other_animals";
    value: "yes" | "no" | "unknown";
}>;

const ASKED_COMPANIONS = [
    { type: "children", word: "kids" },
    { type: "cats", word: "cats" },
] as const;

function unknownCompanions(
    goodWith: string[] | undefined,
    getsAlongWith: GetsAlongWith | undefined,
) {
    return ASKED_COMPANIONS.filter(
        ({ type, word }) =>
            !goodWith?.some((label) => label.toLowerCase() === word) &&
            !getsAlongWith?.some(
                (entry) => entry.type === type && entry.value !== "unknown",
            ),
    ).map(({ word }) => word);
}

function armFor(assigned: string | null) {
    return assigned === "control" ||
        assigned === "reassurance" ||
        assigned === "facts"
        ? assigned
        : null;
}

function providerKindFor(providerType: ProviderType) {
    if (providerType === ProviderType.ShelterRescue) return "shelter_rescue";
    if (providerType === ProviderType.Breeder) return "breeder";
    return null;
}

export type ListingFactRow =
    | { label: string; value: string; gap?: undefined }
    | {
          label: string;
          value?: undefined;
          gap: ListingContactGap;
          question: string;
      };

function fact(label: string, value: string | null | undefined) {
    return value ? { label, value } : undefined;
}

function factRows(
    data: ListingData,
    inclusions: FeeInclusions,
    gapRow: (gap: ListingContactGap) => ListingFactRow | undefined,
) {
    const feeCovers =
        inclusions.source === "listing"
            ? formatIncludedList(inclusions.items)
            : null;
    return [
        fact("Breed", data.breedName),
        fact("Age", joinParts([data.ageLabel, data.sexLabel])),
        fact("Size", joinParts([data.sizeLabel, data.weightRange])),
        fact("Good with", goodWithValue(data.goodWith)),
        gapRow("goodWith"),
        feeCovers ? fact("Fee covers", feeCovers) : gapRow("fee"),
        gapRow("spay"),
        gapRow("meeting"),
        fact("From", joinParts([data.city, data.distanceLabel])),
    ].filter((row): row is ListingFactRow => row !== undefined);
}

function spayWords(sexLabel?: string) {
    const sex = sexLabel?.toLowerCase();
    if (sex === "male") return { label: "Neutered", word: "neutered" };
    if (sex === "female") return { label: "Spayed", word: "spayed" };
    return { label: "Spayed/neutered", word: "spayed or neutered" };
}

function listingFacts({
    data,
    inclusions,
    providerType,
    providerName,
    included,
    getsAlongWith,
}: {
    data: ListingData;
    inclusions: FeeInclusions;
    providerType: ProviderType;
    providerName?: string;
    included: string[];
    getsAlongWith?: GetsAlongWith;
}) {
    const health = deriveListingHealth({
        providerKind: providerKindFor(providerType),
        sexLabel: data.sexLabel,
        providerName,
        healthTests: data.healthTests ?? [],
        completedCare: data.completedCare ?? [],
        reportedHealth: data.reportedHealth ?? [],
        included,
        description: data.description ?? "",
    });
    const spayMissing =
        health?.care.find((chip) => chip.key === "spay")?.status ===
        "not_listed";
    const answers = listingContactAnswers({ data, inclusions });
    const companions = unknownCompanions(data.goodWith, getsAlongWith).join(
        " and ",
    );
    const spay = spayWords(data.sexLabel);
    const gapQuestions: Record<ListingContactGap, string> = {
        fee: answers.fee.followUpQuestion,
        goodWith: `Is ${data.dogName ?? "this pup"} good with ${companions}?`,
        spay: `Is ${data.dogName ?? "this pup"} ${spay.word}?`,
        meeting: answers.meeting.followUpQuestion,
    };
    const gapLabels: Record<ListingContactGap, string> = {
        fee: "Fee covers",
        goodWith: `Good with ${companions.replace(" and ", " & ")}`,
        spay: spay.label,
        meeting: "Meet before deciding",
    };
    const shown: Record<ListingContactGap, boolean> = {
        fee: inclusions.source !== "listing",
        goodWith: companions.length > 0,
        spay: spayMissing,
        meeting: true,
    };
    const gaps = GAP_ORDER.filter((gap) => shown[gap]).slice(0, MAX_GAPS);
    return {
        rows: factRows(data, inclusions, (gap) =>
            gaps.includes(gap)
                ? { label: gapLabels[gap], gap, question: gapQuestions[gap] }
                : undefined,
        ),
        gaps,
        gapQuestions,
    };
}

function askFor(
    dogName: string,
    selected: readonly ListingContactGap[],
    gapQuestions: Record<ListingContactGap, string>,
) {
    if (selected.length === 0) return null;
    const noun = selected.length === 1 ? "question" : "questions";
    return {
        label: `Ask ${selected.length} ${noun} about ${dogName}`,
        question: GAP_ORDER.filter((gap) => selected.includes(gap))
            .map((gap) => gapQuestions[gap])
            .join(" "),
    };
}

export function useListingContactHelp({
    sections,
    providerName,
    eligible,
}: {
    sections: DogPageSectionsProps;
    providerName?: string;
    eligible: boolean;
}) {
    const {
        dogId,
        dogName,
        listing,
        listingPageV2,
        providerProfile,
        providerType,
        copilotEnabled,
    } = sections;
    const preview = useListingContactHelpPreview();
    const live = eligible && copilotEnabled;
    const posthog = usePostHog();
    const [visibleListing, setVisibleListing] = useState<string | null>(null);
    const [picked, setPicked] = useState<{
        dogId: string;
        gaps: ListingContactGap[];
    }>({ dogId, gaps: [] });
    const viewed = useRef(new Set<string>());
    const assigned = usePreviewedExperiment("listing-contact-help", {
        eligible: live,
        measured: visibleListing === dogId,
        preview,
    });
    const arm = armFor(assigned);
    const capture = (input: ListingContactHelpEvent) => {
        if (preview !== null || !live || arm === null) return;
        const { event, ...properties } =
            listingContactHelpEventSchema.parse(input);
        posthog.capture(event, properties);
    };
    const onVisible = (surface: "desktop" | "mobile") => {
        if (arm === null) return;
        const key = `${dogId}:${arm}`;
        if (viewed.current.has(key)) return;
        viewed.current.add(key);
        setVisibleListing(dogId);
        capture({ event: "listing_contact_help_viewed", dogId, arm, surface });
    };
    const data = { ...listing, dogName };
    const inclusions = listingFeeInclusions({
        listingIncluded: listingPageV2?.application?.included,
        providerIncluded: providerProfile?.pricing.fees[0]?.includes,
    });
    const { rows, gaps, gapQuestions } = listingFacts({
        data,
        inclusions,
        providerType,
        providerName,
        included: listingPageV2?.application?.included ?? [],
        getsAlongWith: listingPageV2?.listing.facts.getsAlongWith,
    });
    const selected =
        arm === "facts" && picked.dogId === dogId
            ? picked.gaps.filter((gap) => gaps.includes(gap))
            : [];
    const toggleGap = (
        gap: ListingContactGap,
        surface: "desktop" | "mobile",
    ) => {
        const on = !selected.includes(gap);
        setPicked((prev) => {
            const current = prev.dogId === dogId ? prev.gaps : [];
            return {
                dogId,
                gaps: on
                    ? [...current.filter((g) => g !== gap), gap]
                    : current.filter((g) => g !== gap),
            };
        });
        capture({
            event: "listing_contact_help_gap_toggled",
            dogId,
            arm: "facts",
            gap,
            selected: on,
            surface,
        });
    };
    const ask = askFor(dogName, selected, gapQuestions);
    return {
        providerName,
        facts: { rows, selected, toggleGap },
        ask,
        experiment: { arm, onVisible },
    };
}
